// Uses a month grid for all three report period sizes.
// With Days selected, users click individual dates. With Weeks they click week rows;
// with Months they click the whole month. The surrounding dates stay visible so users
// can see where each selected period falls on the calendar.
import { DateTime } from 'luxon';
import { Resolution } from '../report-settings/reportSettings.ts';
import { DraftRange } from './draftRange.ts';
import { getInitialPeriodClass } from './rangePreview.ts';

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];


interface MonthProps {
    month: DateTime;
    resolution: Resolution;
    periodIndexByStart: Map<string, number>;
    draft: DraftRange;
    todayIsoDate: string;
    // Pointer previews need one element per selectable report period. Register only
    // its selectable day, week row, or month, not its repeats in adjacent month grids.
    registerCell: (periodIndex: number, element: HTMLElement | null) => void;
}

export function CalendarMonth(props: MonthProps) {
    const { month, resolution } = props;
    const monthPeriodIndex = resolution === 'Months'
        ? props.periodIndexByStart.get(month.toISODate()!) ?? -1
        : -1;
    const monthClassName = [
        'report-calendar-month',
        resolution === 'Months' ? 'report-calendar-month--selectable' : '',
        resolution === 'Months' && monthPeriodIndex === -1 ? 'is-disabled' : '',
        resolution === 'Months' ? getInitialPeriodClass(monthPeriodIndex, props.draft) : '',
    ].filter(Boolean).join(' ');
    const content = (
        <>
            <div className='report-calendar-month__title'>{month.toFormat('LLLL')}</div>
            <div className='report-calendar-month__weekdays'>
                <span className='report-calendar-week-number-heading'>#</span>
                {WEEKDAYS.map((weekday) => <span key={weekday}>{weekday}</span>)}
            </div>
            <CalendarWeekRows {...props} />
        </>
    );

    // With Months selected, clicking anywhere in this month selects its report period.
    // The week rows and day numbers inside it are labels, not separate selectable dates.
    const isMonthSelection = resolution === 'Months';
    const calendarMonth = isMonthSelection ? (
        <div
            role='button'
            tabIndex={monthPeriodIndex === -1 ? -1 : 0}
            aria-disabled={monthPeriodIndex === -1}
            className={monthClassName}
            data-period-index={monthPeriodIndex === -1 ? undefined : monthPeriodIndex}
            ref={(element) => {
                if (monthPeriodIndex !== -1) props.registerCell(monthPeriodIndex, element);
            }}
            onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                event.currentTarget.click();
            }}
        >
            {content}
        </div>
    ) : <div className={monthClassName}>{content}</div>;
    return calendarMonth;
}

function CalendarWeekRows(props: MonthProps) {
    const firstWeek = props.month.startOf('month').startOf('week');
    const weeks = Array.from({ length: 6 }, (_, offset) => firstWeek.plus({ weeks: offset }));

    return (
        <div className={`report-calendar-weeks report-calendar-weeks--${props.resolution.toLowerCase()}`}>
            {weeks.map((weekStart) => {
                // Weeks can span two months. Assign each to its Thursday's month to avoid
                // duplicate selectable rows and duplicate refs for the same period.
                const isWeekInMonth = weekStart.plus({ days: 3 }).month === props.month.month;
                const weekPeriodIndex = isWeekInMonth && props.resolution === 'Weeks'
                    ? props.periodIndexByStart.get(weekStart.toISODate()!) ?? -1
                    : -1;
                const weekContent = (
                    <>
                        <strong className='report-calendar-week-number'>{weekStart.weekNumber}</strong>
                        {Array.from({ length: 7 }, (_, dayOffset) => {
                            const date = weekStart.plus({ days: dayOffset });
                            const isCurrentMonth = date.month === props.month.month;
                            return (
                                <CalendarDay
                                    key={date.toISODate()}
                                    date={date}
                                    isCurrentMonth={isCurrentMonth}
                                    {...props}
                                />
                            );
                        })}
                    </>
                );

                const weekRow = props.resolution === 'Weeks' ? (
                    <button
                        type='button'
                        key={weekStart.toISODate()}
                        className={getInitialPeriodClass(weekPeriodIndex, props.draft)}
                        disabled={weekPeriodIndex === -1}
                        data-period-index={weekPeriodIndex === -1 ? undefined : weekPeriodIndex}
                        ref={(element) => {
                            if (weekPeriodIndex !== -1) props.registerCell(weekPeriodIndex, element);
                        }}
                    >
                        {weekContent}
                    </button>
                ) : (
                    <div className='report-calendar-week-row' key={weekStart.toISODate()}>
                        {weekContent}
                    </div>
                );
                return weekRow;
            })}
        </div>
    );
}

function CalendarDay({
    date,
    isCurrentMonth,
    resolution,
    periodIndexByStart,
    draft,
    registerCell,
    todayIsoDate,
}: MonthProps & { date: DateTime; isCurrentMonth: boolean }) {
    const isToday = isCurrentMonth && date.toISODate() === todayIsoDate;
    const periodIndex = isCurrentMonth && resolution === 'Days'
        ? periodIndexByStart.get(date.toISODate()!) ?? -1
        : -1;
    const className = [
        resolution === 'Days' ? getInitialPeriodClass(periodIndex, draft) : '',
        isToday ? 'today' : '',
        !isCurrentMonth ? 'outside-month' : '',
    ].filter(Boolean).join(' ');

    // Adjacent months repeat these dates. Only their own month registers a selectable
    // button, so a day's period index never points to two rendered cells.
    if (resolution === 'Days' && isCurrentMonth) {
        return (
            <button
                type='button'
                className={className}
                disabled={periodIndex === -1}
                data-period-index={periodIndex === -1 ? undefined : periodIndex}
                ref={(element) => {
                    if (periodIndex !== -1) registerCell(periodIndex, element);
                }}
            >
                {date.day}
            </button>
        );
    }

    return (
        <span className={className}>
            {date.day}
        </span>
    );
}
